import React, { useState, useEffect, useRef } from 'react';
import './scss/ListAction.scss';
import { config } from '../../config';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { useBoard } from '../../context/BoardContext';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { useTheme } from '../../context/ThemeContext';
import {
    getPaletteForTheme,
    findPaletteIndexByColor,
    normalizeTheme
} from '../../utils/listColorMapping';

import { incrementTotalCards } from '../../redux/Slices/actionlistSlice';
import Close from '../icons/Close';

const MainScreen = ({ onNavigate, listId, boardId, workspaceId }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isArchivingCards, setIsArchivingCards] = useState(false);
    const [isWatching, setIsWatching] = useState(false);
    const [watchers, setWatchers] = useState([]);
    const [isColorPaletteExpanded, setIsColorPaletteExpanded] = useState(false);
    const [isColorPaletteOpen, setIsColorPaletteOpen] = useState(false);

    const { boardState, setBoard } = useBoard();
    const { theme } = useTheme();
    const dispatch = useDispatch();

    // Track the actual resolved theme (handles "system" theme properly)
    const [actualTheme, setActualTheme] = useState(() => normalizeTheme(theme));

    // Listen for data-theme attribute changes (which ThemeContext manages)
    useEffect(() => {
        const updateActualTheme = () => {
            const newActualTheme = normalizeTheme(theme);
            setActualTheme(newActualTheme);
        };

        // Update immediately
        updateActualTheme();

        // Listen for data-theme attribute changes on document element
        // This will catch both manual theme changes and system theme changes
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                    updateActualTheme();
                }
            });
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-theme']
        });

        return () => observer.disconnect();
    }, [theme]);


    const onAchiveList = (listId) => setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.filter((list) => list._id !== listId),
    }));

    const onArchiveListAllCards = (listId) => setBoard((prev) => ({
        ...prev,
        actionLists: prev.actionLists.map((list) =>
            list._id === listId ? { ...list, cards: [] } : list
        ),
    }));

    // Workspace plan gating and color options
    const workspaces = useSelector(state => state.workspaces.workspaces);
    const workspace = workspaces?.find(ws => ws.shortId === workspaceId);
    const planType = workspace?.subscription?.planType || 'Free';
    const isPro = ['Standard', 'Premium', 'Enterprise', 'AppSumo'].includes(planType);

    // Get theme-aware color palette using the actual resolved theme
    const COLOR_PALETTE = getPaletteForTheme(actualTheme);



    // Get current list to check selected color
    const currentList = boardState?.actionLists?.find(list => list._id === listId);
    const currentListColor = currentList?.color;

    // Find the current color's index in the palette for proper selection highlighting
    const currentColorIndex = findPaletteIndexByColor(currentListColor);

    const handleSetListColor = async (colorData) => {
        const newColor = typeof colorData === 'string' ? colorData : colorData.bg;
        const newTextColor = typeof colorData === 'object' ? colorData.text : null;

        // Optimistic update
        let prevColor = null;
        let prevTextColor = null;
        setBoard(prev => {
            const lists = prev?.actionLists || [];
            const current = lists.find(l => l._id === listId);
            prevColor = current?.color || null;
            prevTextColor = current?.textColor || null;
            return {
                ...prev,
                actionLists: lists.map(l => l._id === listId ? {
                    ...l,
                    color: newColor,
                    textColor: newTextColor
                } : l)
            };
        });
        try {
            const response = await fetch(`${config.API_URI}/v1/actionLists/${listId}`, {
                method: 'PUT',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ boardId, color: newColor, textColor: newTextColor })
            });
        } catch (error) {
            // Revert on failure
            setBoard(prev => ({
                ...prev,
                actionLists: (prev?.actionLists || []).map(l => l._id === listId ? {
                    ...l,
                    color: prevColor,
                    textColor: prevTextColor
                } : l)
            }));
            showError(dispatch, 'Error updating color: ' + error.message);
        }
    };

    const handleResetColor = () => handleSetListColor({ bg: null, text: null });



    const handleArchivedList = async () => {
        setIsArchiving(true);
        try {
            const response = await fetch(`${config.API_URI}/v1/actionLists/archive/${listId}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ boardId, archivedStatus: true })
            });

            if (!response.ok) throw new Error('Failed to archive the list');

            onAchiveList(listId);

            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));

            showSuccess(dispatch, 'List archived successfully!');
        } catch (error) {
            showError(dispatch, 'Error archiving the list: ' + error.message);
        } finally {
            setIsArchiving(false);
        }
    };

    const handleWatchActionList = async () => {
        setIsWatching(true);
        try {

            const response = await fetch(`${config.API_URI}/v1/actionLists/${listId}/watch`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ boardId })

            });

            if (!response.ok) throw new Error('Failed to toggle watch status');

            const data = await response.json();
            showSuccess(dispatch, data.message);
            setWatchers(data.watchers);
        } catch (error) {
            showError(dispatch, 'Error toggling watch status: ' + error.message);
        } finally {
            setIsWatching(false);
        }
    };

    const handleArchivedAllCards = async () => {
        setIsArchivingCards(true);
        try {

            const response = await fetch(config.API_URI + `/v1/actionLists/${listId}/cards/archived`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ boardId })

            });

            if (!response.ok) throw new Error('Failed to archive the list');

            onArchiveListAllCards(listId);
            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));


            showSuccess(dispatch, 'All cards archived successfully!');
        } catch (error) {
            showError(dispatch, 'Error archiving the list: ' + error.message);
        } finally {
            setIsArchivingCards(false);
        }
    };

    return (
        <>
            <h2>List Actions</h2>
            <ul className="actions-list">
                <li onClick={() => onNavigate('add')}>Add Card</li>
                {/* <li onClick={() => onNavigate('copy')}>Copy List</li> */}
                {/* <li onClick={handleWatchActionList}>{isWatching ? 'Notifying...' : 'Notify'}</li> */}
            </ul>

            <ul className="actions-list">
                <li className="archive" onClick={handleArchivedList}>{isArchiving ? 'Archiving...' : 'Archive this List'}</li>
                <li className="archive" onClick={handleArchivedAllCards}>{isArchiving ? 'Archiving...' : 'Archive all cards in this List'}</li>
                <li>
                    <div
                        className="color-palette-header"
                        onClick={() => setIsColorPaletteOpen(!isColorPaletteOpen)}
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            fontWeight: 400
                        }}
                    >
                        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 400 }}>List color</h3>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                            {isColorPaletteOpen ? 'expand_less' : 'expand_more'}
                        </span>
                    </div>
                </li>
            </ul>




            {isColorPaletteOpen && (
                <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>
                    Upgrade your workspace to change list colors.
                </div>
            )}
            {isColorPaletteOpen && (
                <div className="color-palette">
                    <div className="color-grid">
                        {(isColorPaletteExpanded ? COLOR_PALETTE : COLOR_PALETTE.slice(0, 10)).map((colorData, arrayIndex) => {
                            // Use the stable index from the color mapping for proper selection
                            const isSelected = currentColorIndex === colorData.index;
                            return (
                                <button
                                    key={`${colorData.index}-${colorData.bg}`}
                                    className={`color-box ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleSetListColor(colorData)}
                                    style={{
                                        ...(colorData.bg.startsWith("linear-gradient")
                                            ? { background: colorData.bg } // ✅ gradients
                                            : { backgroundColor: colorData.bg } // ✅ solid colors
                                        ),
                                        color: colorData.text
                                    }}
                                    aria-label={`Set list color ${colorData.bg}`}
                                    title={`Set list color to ${colorData.bg}`}
                                >
                                    {isSelected && (
                                        <span className="material-symbols-outlined selected-icon">
                                            check
                                        </span>
                                    )}
                                </button>

                            );
                        })}
                    </div>
                    {COLOR_PALETTE.length > 10 && (
                        <div className="color-expand-section">
                            <button
                                className="expand-color-button"
                                onClick={() => setIsColorPaletteExpanded(!isColorPaletteExpanded)}
                                aria-label={isColorPaletteExpanded ? 'Show fewer colors' : 'Show more colors'}
                            >
                                <span className="material-symbols-outlined">
                                    {isColorPaletteExpanded ? 'expand_less' : 'expand_more'}
                                </span>
                                <span>{isColorPaletteExpanded ? 'Show fewer colors' : 'Show more colors'}</span>
                            </button>
                        </div>
                    )}
                    <div className="color-actions">
                        <button
                            className="reset-color-button"
                            onClick={() => handleResetColor()}
                            aria-label="Reset list color"
                            title={isPro ? 'Reset list color' : 'Upgrade to change list colors'}
                        // disabled={!isPro}
                        >
                            <Close size={20} />
                            <span>Remove color</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const AddCard = ({ onBack, listId, boardId }) => {
    const textareaRef = useRef(null);
    const [title, setTitle] = useState('');
    const { setBoard } = useBoard();
    const dispatch = useDispatch();

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const handleAddCard = async () => {
        if (!title.trim()) return;
        const position = 'top';
        try {

            const response = await fetch(`${config.API_URI}/v1/cards`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({
                    actionListId: listId,
                    title: title,
                    description: '',
                    position,
                    users: [],
                }),
            });
            if (!response.ok) throw new Error('Failed to add card');

            const { card } = await response.json();
            setBoard((prev) => ({
                ...prev,
                actionLists: prev.actionLists.map(list =>
                    list._id === listId
                        ? {
                            ...list,
                            cards: position === 'top'
                                ? [card, ...list.cards]  // Add at the top
                                : [...list.cards, card], // Add at the bottom
                        }
                        : list
                ),
            }));
            dispatch(incrementTotalCards({ actionListId: listId }));
            showSuccess(dispatch, 'Card added successfully!');
            onBack();
        } catch (error) {
            showError(dispatch, 'Error adding card: ' + error.message);
        }
    };

    return (
        <>
            <h2>
                <button className="back-button" onClick={onBack}>
                    <span className="material-symbols-outlined">keyboard_backspace</span>
                </button>
                Add Card
            </h2>
            <div className="copy-list-form">
                <textarea ref={textareaRef} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter card title"></textarea>
                <button className="confirm-button" onClick={handleAddCard}>Add Card</button>
            </div>
        </>
    );
};

const CopyListScreen = ({ onBack, listId, boardId }) => {
    const textareaRef = useRef(null);
    const [title, setTitle] = useState('');
    const { setBoard } = useBoard();
    const dispatch = useDispatch();

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const handleCopyList = async () => {
        if (!title.trim()) return;
        try {

            const response = await fetch(`${config.API_URI}/v1/actionLists/${listId}/copy`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',

                },
                credentials: 'include',
                body: JSON.stringify({ boardId, title }),
            });
            if (!response.ok) throw new Error('Failed to copy list');

            const copiedList = await response.json();
            setBoard((prev) => ({
                ...prev,
                actionLists: [...prev.actionLists, copiedList],
            }));
            showSuccess(dispatch, 'List copied successfully!');
            onBack();
        } catch (error) {
            showError(dispatch, 'Error copying list: ' + error.message);
        }
    };

    return (
        <>
            <h2>
                <button className="back-button" onClick={onBack}>
                    <span className="material-symbols-outlined">keyboard_backspace</span>
                </button>
                Copy List
            </h2>
            <div className="copy-list-form">
                <textarea ref={textareaRef} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter list title"></textarea>
                <button className="confirm-button" onClick={handleCopyList}>Create</button>
            </div>
        </>
    );
};

const ListAction = ({ listId, boardId, workspaceId }) => {
    const [screen, setScreen] = useState('main');

    const renderScreen = () => {
        switch (screen) {
            case 'copy':
                return <CopyListScreen onBack={() => setScreen('main')} listId={listId} boardId={boardId} />;
            case 'add':
                return <AddCard onBack={() => setScreen('main')} listId={listId} boardId={boardId} />;
            default:
                return <MainScreen onNavigate={setScreen} listId={listId} boardId={boardId} workspaceId={workspaceId} />;
        }
    };

    return <div className="list-actions">{renderScreen()}</div>;
};

export default ListAction;
